import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, LogOut, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/site";
import Rating from "@/components/Rating";

const EMPTY_PRODUCT = {
  name: "",
  category: "Necklaces",
  description: "",
  price: "",
  mrp: "",
  sku: "",
  active: true,
  finish: "Antique Gold Plated",
  gross_weight_g: "",
  stock_status: "in_stock",
  featured: false,
  images: "",
};

const EMPTY_VIDEO = { title: "", reel_url: "", product_id: "", active: true, video_file: null, poster_file: null };

function VideoForm({ products, initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave(form);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-[#1C1712]/60 backdrop-blur-sm p-4 sm:p-10" data-testid="admin-video-modal">
      <form onSubmit={submit} className="mx-auto max-w-xl card-surface p-6 sm:p-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="overline-tag">{initial._id ? "Edit" : "New"} Video</p>
            <h3 className="mt-1 font-display text-2xl font-semibold">{initial.title || "Untitled reel"}</h3>
          </div>
          <button type="button" onClick={onClose} className="h-9 w-9 grid place-items-center text-ink2 hover:text-gold" data-testid="admin-video-modal-close"><X size={18} /></button>
        </div>
        <div className="gold-hr my-5" />
        <div className="grid gap-4">
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Title / caption *</span>
            <input required className="field mt-1.5" value={form.title} onChange={set("title")} data-testid="admin-video-title" />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Instagram reel URL</span>
            <input className="field mt-1.5 font-mono text-xs" placeholder="https://www.instagram.com/reel/…" value={form.reel_url || ""} onChange={set("reel_url")} data-testid="admin-video-reel-url" />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>…or upload MP4/WebM (max 80MB)</span>
            <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={(e) => setForm({ ...form, video_file: e.target.files?.[0] || null })} className="field mt-1.5 text-xs" data-testid="admin-video-file" />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Poster / thumbnail image (optional)</span>
            <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, poster_file: e.target.files?.[0] || null })} className="field mt-1.5 text-xs" data-testid="admin-video-poster" />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Featured product (for Shop This Look)</span>
            <select className="field mt-1.5" value={form.product_id || ""} onChange={set("product_id")} data-testid="admin-video-product">
              <option value="">— none —</option>
              {products.map((p) => <option key={p._id} value={p._id}>{p.name} · {inr(p.price)}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#9A792F]" data-testid="admin-video-active" />
            <span className="text-sm">Visible on site</span>
          </label>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button type="submit" className="btn-gold flex-1 justify-center" disabled={busy} data-testid="admin-video-save">{busy ? "Uploading…" : "Save Video"}</button>
          <button type="button" onClick={onClose} className="btn-ghost-gold justify-center">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function ProductForm({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: form.name,
      category: form.category,
      description: form.description,
      price: parseFloat(form.price) || 0,
      mrp: form.mrp ? parseFloat(form.mrp) : null,
      sku: form.sku || "",
      active: form.active !== false,
      finish: form.finish,
      gross_weight_g: parseFloat(form.gross_weight_g) || 0,
      stock_status: form.stock_status,
      featured: !!form.featured,
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await onSave(payload);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-[#1C1712]/60 backdrop-blur-sm p-4 sm:p-10" data-testid="admin-product-modal">
      <form onSubmit={submit} className="mx-auto max-w-2xl card-surface p-6 sm:p-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="overline-tag">{initial._id ? "Edit" : "New"} Piece</p>
            <h3 className="mt-1 font-display text-2xl font-semibold">{initial.name || "Untitled jewel"}</h3>
          </div>
          <button type="button" onClick={onClose} className="h-9 w-9 grid place-items-center text-ink2 hover:text-gold" data-testid="admin-product-modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="gold-hr my-5" />
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2 block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Name</span>
            <input required className="field mt-1.5" value={form.name} onChange={set("name")} data-testid="admin-product-name" />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Category</span>
            <select className="field mt-1.5" value={form.category} onChange={set("category")} data-testid="admin-product-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Selling price (₹)</span>
            <input required type="number" min="0" step="1" className="field mt-1.5" value={form.price} onChange={set("price")} data-testid="admin-product-price" />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>MRP (₹)</span>
            <input type="number" min="0" step="1" className="field mt-1.5" value={form.mrp || ""} onChange={set("mrp")} data-testid="admin-product-mrp" />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>SKU</span>
            <input className="field mt-1.5 font-mono text-xs" value={form.sku || ""} onChange={set("sku")} data-testid="admin-product-sku" />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Finish / Plating</span>
            <input className="field mt-1.5" value={form.finish} onChange={set("finish")} />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Weight (g)</span>
            <input type="number" min="0" step="0.1" className="field mt-1.5" value={form.gross_weight_g} onChange={set("gross_weight_g")} />
          </label>
          <label className="block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Stock status</span>
            <select className="field mt-1.5" value={form.stock_status} onChange={set("stock_status")} data-testid="admin-product-stock">
              <option value="in_stock">In Stock</option>
              <option value="made_to_order">Made to Order</option>
              <option value="sold_out">Sold Out</option>
            </select>
          </label>
          <div className="flex flex-wrap items-center gap-6 sm:mt-1.5">
            <label className="flex items-center gap-2.5">
              <input type="checkbox" checked={!!form.featured} onChange={set("featured")} className="h-4 w-4 accent-[#9A792F]" data-testid="admin-product-featured" />
              <span className="text-sm">Featured</span>
            </label>
            <label className="flex items-center gap-2.5">
              <input type="checkbox" checked={form.active !== false} onChange={set("active")} className="h-4 w-4 accent-[#9A792F]" data-testid="admin-product-active" />
              <span className="text-sm">Active (visible in store)</span>
            </label>
          </div>
          <label className="sm:col-span-2 block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Description</span>
            <textarea rows={3} className="field mt-1.5 resize-none" value={form.description} onChange={set("description")} data-testid="admin-product-description" />
          </label>
          <label className="sm:col-span-2 block">
            <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Image URLs (one per line)</span>
            <textarea rows={3} className="field mt-1.5 font-mono text-xs" value={form.images} onChange={set("images")} placeholder="/images/… or https://…" data-testid="admin-product-images" />
          </label>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button type="submit" className="btn-gold flex-1 justify-center" disabled={busy} data-testid="admin-product-save">
            {busy ? "Saving…" : "Save Piece"}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost-gold justify-center">Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState(null);
  const [enquiries, setEnquiries] = useState(null);
  const [orders, setOrders] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [videos, setVideos] = useState(null);
  const [requests, setRequests] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);

  const isAdmin = !!(user && user.role === "admin");

  const loadAll = () => {
    api.get("/products").then((d) => setProducts(d.data)).catch(() => setProducts([]));
    api.get("/admin/enquiries").then((d) => setEnquiries(d.data)).catch(() => setEnquiries([]));
    api.get("/admin/orders").then((d) => setOrders(d.data)).catch(() => setOrders([]));
    api.get("/admin/reviews").then((d) => setReviews(d.data)).catch(() => setReviews([]));
    api.get("/admin/videos").then((d) => setVideos(d.data)).catch(() => setVideos([]));
    api.get("/admin/custom-requests").then((d) => setRequests(d.data)).catch(() => setRequests([]));
  };

  useEffect(() => {
    if (isAdmin) loadAll();
  }, [isAdmin]);

  if (user === null) {
    return <div className="min-h-screen grid place-items-center bg-bg2"><p className="font-display text-2xl text-ink2">Opening the atelier…</p></div>;
  }
  if (!isAdmin) return <Navigate to="/admin" replace />;

  const saveProduct = async (payload) => {
    try {
      if (editing._id) {
        await api.put(`/admin/products/${editing._id}`, payload);
        toast.success("Piece updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Piece added to the atelier");
      }
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the atelier?`)) return;
    try {
      await api.delete(`/admin/products/${p._id}`);
      toast.success("Piece removed");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setEnquiryStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((es) => es.map((e) => (e._id === id ? { ...e, status } : e)));
      toast.success("Enquiry updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setOrderStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status });
      setOrders((os) => os.map((o) => (o.order_id === orderId ? { ...o, status } : o)));
      toast.success("Order updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveTracking = async (orderId, trackingNumber) => {
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}`, { tracking_number: trackingNumber });
      setOrders((os) => os.map((o) => (o.order_id === orderId ? { ...o, tracking_number: data.tracking_number } : o)));
      toast.success(trackingNumber ? "Tracking saved" : "Tracking cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setReviewApproval = async (r, approved) => {
    try {
      const { data } = await api.patch(`/admin/reviews/${r._id}`, { approved });
      setReviews((rs) => rs.map((x) => (x._id === r._id ? data : x)));
      toast.success(approved ? "Review published" : "Review unpublished");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteReview = async (r) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/admin/reviews/${r._id}`);
      setReviews((rs) => rs.filter((x) => x._id !== r._id));
      toast.success("Review deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveVideo = async (form) => {
    try {
      if (form._id) {
        await api.patch(`/admin/videos/${form._id}`, {
          title: form.title,
          reel_url: form.reel_url || "",
          product_id: form.product_id || null,
          active: form.active !== false,
        });
        toast.success("Video updated");
      } else {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("reel_url", form.reel_url || "");
        fd.append("product_id", form.product_id || "");
        if (form.video_file) fd.append("video_file", form.video_file);
        if (form.poster_file) fd.append("poster_file", form.poster_file);
        await api.post("/admin/videos", fd);
        toast.success("Video added");
      }
      setEditingVideo(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteVideo = async (v) => {
    if (!window.confirm(`Remove "${v.title}"?`)) return;
    try {
      await api.delete(`/admin/videos/${v._id}`);
      setVideos((vs) => vs.filter((x) => x._id !== v._id));
      toast.success("Video removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const moveVideo = async (v, dir) => {
    const sorted = [...videos].sort((a, b) => a.sort_order - b.sort_order);
    const i = sorted.findIndex((x) => x._id === v._id);
    const j = i + dir;
    if (j < 0 || j >= sorted.length) return;
    try {
      await api.patch(`/admin/videos/${sorted[i]._id}`, { sort_order: sorted[j].sort_order });
      await api.patch(`/admin/videos/${sorted[j]._id}`, { sort_order: sorted[i].sort_order });
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setRequestStatus = async (id, status) => {
    try {
      await api.patch(`/admin/custom-requests/${id}`, { status });
      setRequests((rs) => rs.map((r) => (r._id === id ? { ...r, status } : r)));
      toast.success("Request updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const TABS = [
    { key: "products", label: "Pieces", data: products },
    { key: "enquiries", label: "Enquiries", data: enquiries },
    { key: "orders", label: "Orders", data: orders },
    { key: "videos", label: "Videos", data: videos },
    { key: "requests", label: "Custom Requests", data: requests },
    { key: "reviews", label: "Reviews", data: reviews },
  ];

  return (
    <div className="min-h-screen bg-bg pb-24" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-gold/25 bg-surface/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-4 px-4 sm:px-8 py-4">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="Sashti Jewels" className="h-10 w-10 rounded-full object-cover border border-gold/40" />
            <div>
              <p className="font-display text-xl font-semibold leading-none">Admin Atelier</p>
              <p className="mt-1 font-mono text-[10px] text-ink3">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:block font-cinzel text-[10px] uppercase text-ink2 hover:text-gold px-4 py-2.5" style={{ letterSpacing: "0.2em" }} data-testid="admin-view-store">View Store</Link>
            <button onClick={logout} className="flex items-center gap-2 border border-line px-4 py-2.5 font-cinzel text-[10px] uppercase text-ink2 hover:border-gold hover:text-gold transition-colors" style={{ letterSpacing: "0.2em" }} data-testid="admin-logout">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1280px] px-4 sm:px-8 pt-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`border px-4 py-2.5 font-cinzel text-[10px] uppercase transition-all duration-300 ${
                  tab === t.key ? "border-gold bg-gold text-[#241A08]" : "border-line text-ink2 hover:border-gold hover:text-gold"
                }`}
                style={{ letterSpacing: "0.16em" }}
                data-testid={`admin-tab-${t.key}`}
              >
                {t.label}{t.data ? ` · ${t.data.length}` : ""}
              </button>
            ))}
          </div>
          {tab === "products" && (
            <button onClick={() => setEditing({ ...EMPTY_PRODUCT })} className="btn-gold !px-5 !py-2.5" data-testid="admin-add-product-btn">
              <Plus size={14} /> Add Piece
            </button>
          )}
          {tab === "videos" && (
            <button onClick={() => setEditingVideo({ ...EMPTY_VIDEO })} className="btn-gold !px-5 !py-2.5" data-testid="admin-add-video-btn">
              <Plus size={14} /> Add Video
            </button>
          )}
        </div>

        {tab === "products" && (
          <div className="mt-8 space-y-3" data-testid="admin-products-list">
            {products === null && <p className="text-sm text-ink3 font-mono">Loading pieces…</p>}
            {products && products.length === 0 && <p className="text-sm text-ink3 font-mono">No pieces yet — add your first jewel.</p>}
            {products && products.map((p) => (
              <div key={p._id} className="card-surface flex items-center gap-4 p-3.5" data-testid={`admin-product-row-${p._id}`}>
                <div className="h-16 w-14 shrink-0 overflow-hidden bg-bg2 border border-line">
                  {p.images[0] ? <img src={p.images[0]} alt="" className="h-full w-full object-contain" /> : null}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg font-semibold truncate">{p.name}</p>
                  <p className="font-mono text-[10px] uppercase text-ink3">
                    {p.sku ? `${p.sku} · ` : ""}{p.category} · {p.finish} · {p.stock_status.replace(/_/g, " ")}{p.featured ? " · featured" : ""}{p.active === false ? " · INACTIVE" : ""}
                  </p>
                </div>
                <span className="font-mono text-sm text-ink hidden sm:block">{inr(p.price)}</span>
                <button onClick={() => setEditing({ ...p, images: (p.images || []).join("\n"), price: String(p.price), mrp: p.mrp != null ? String(p.mrp) : "", gross_weight_g: String(p.gross_weight_g) })} className="h-9 w-9 grid place-items-center border border-line text-ink2 hover:border-gold hover:text-gold" aria-label="Edit" data-testid={`admin-edit-product-${p._id}`}>
                  <Pencil size={13} />
                </button>
                <button onClick={() => deleteProduct(p)} className="h-9 w-9 grid place-items-center border border-line text-ink2 hover:border-red-400 hover:text-red-600" aria-label="Delete" data-testid={`admin-delete-product-${p._id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-8 space-y-3" data-testid="admin-enquiries-list">
            {enquiries === null && <p className="text-sm text-ink3 font-mono">Loading enquiries…</p>}
            {enquiries && enquiries.length === 0 && <p className="text-sm text-ink3 font-mono">No enquiries yet.</p>}
            {enquiries && enquiries.map((e) => (
              <div key={e._id} className="card-surface p-5" data-testid={`admin-enquiry-${e._id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-semibold">{e.name}</p>
                    <p className="font-mono text-[11px] text-ink3">{e.phone}{e.email ? ` · ${e.email}` : ""}</p>
                  </div>
                  <select value={e.status} onChange={(ev) => setEnquiryStatus(e._id, ev.target.value)} className="border border-line bg-surface px-3 py-2 font-mono text-xs outline-none focus:border-gold" data-testid={`admin-enquiry-status-${e._id}`}>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                {e.product_name && <p className="mt-2 font-display italic text-gold text-base">Re: {e.product_name}</p>}
                <p className="mt-2 text-sm text-ink2 leading-relaxed">{e.message}</p>
                <p className="mt-2 font-mono text-[10px] text-ink3">{e.created_at ? new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-8 space-y-3" data-testid="admin-orders-list">
            {orders === null && <p className="text-sm text-ink3 font-mono">Loading orders…</p>}
            {orders && orders.length === 0 && <p className="text-sm text-ink3 font-mono">No orders yet.</p>}
            {orders && orders.map((o) => (
              <div key={o.order_id} className="card-surface p-5" data-testid={`admin-order-${o.order_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-sm text-ink">{o.order_id}</p>
                    <p className="mt-0.5 font-mono text-[11px] text-ink3">{o.customer?.name} · {o.customer?.phone} · {o.customer?.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-ink">{inr(o.subtotal)}</span>
                    <select value={o.status} onChange={(ev) => setOrderStatus(o.order_id, ev.target.value)} className="border border-line bg-surface px-3 py-2 font-mono text-xs outline-none focus:border-gold" data-testid={`admin-order-status-${o.order_id}`}>
                      <option value="initiated">Initiated</option>
                      <option value="paid">Paid</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {o.items.map((i, idx) => (
                    <span key={idx} className="chip !text-[10px]">{i.name} × {i.quantity}</span>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    defaultValue={o.tracking_number || ""}
                    placeholder="Tracking number"
                    onBlur={(ev) => {
                      const v = ev.target.value.trim();
                      if (v !== (o.tracking_number || "")) saveTracking(o.order_id, v);
                    }}
                    className="w-56 border border-line bg-surface px-3 py-2 font-mono text-xs outline-none focus:border-gold"
                    data-testid={`admin-order-tracking-${o.order_id}`}
                  />
                  <span className="font-mono text-[10px] text-ink3">Saved on click-away · the customer is emailed when you set status “Shipped”</span>
                </div>
                <p className="mt-2 font-mono text-[10px] text-ink3">
                  Payment: {o.status === "paid" ? "paid" : "pending"} · {o.created_at ? new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
                </p>
              </div>
            ))}
          </div>
        )}

        {tab === "videos" && (
          <div className="mt-8 space-y-3" data-testid="admin-videos-list">
            {videos === null && <p className="text-sm text-ink3 font-mono">Loading videos…</p>}
            {videos && videos.length === 0 && <p className="text-sm text-ink3 font-mono">No videos yet — add a reel URL or upload an MP4.</p>}
            {videos && videos.map((v) => (
              <div key={v._id} className="card-surface flex items-center gap-4 p-3.5" data-testid={`admin-video-${v._id}`}>
                <div className="h-16 w-11 shrink-0 overflow-hidden bg-bg2 border border-line">
                  {v.poster_url ? <img src={v.poster_url} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-gold"><Pencil size={0} /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg font-semibold truncate">{v.title}</p>
                  <p className="font-mono text-[10px] text-ink3">
                    {v.video_url ? "MP4 upload" : "Instagram reel"} · order {v.sort_order}{v.product_id ? " · product linked" : ""}{v.active === false ? " · HIDDEN" : ""}
                  </p>
                </div>
                <button onClick={() => moveVideo(v, -1)} className="h-9 w-9 grid place-items-center border border-line text-ink2 hover:border-gold hover:text-gold" aria-label="Move up" data-testid={`admin-video-up-${v._id}`}><ArrowUp size={13} /></button>
                <button onClick={() => moveVideo(v, 1)} className="h-9 w-9 grid place-items-center border border-line text-ink2 hover:border-gold hover:text-gold" aria-label="Move down" data-testid={`admin-video-down-${v._id}`}><ArrowDown size={13} /></button>
                <button onClick={() => setEditingVideo({ ...v })} className="h-9 w-9 grid place-items-center border border-line text-ink2 hover:border-gold hover:text-gold" aria-label="Edit" data-testid={`admin-edit-video-${v._id}`}><Pencil size={13} /></button>
                <button onClick={() => deleteVideo(v)} className="h-9 w-9 grid place-items-center border border-line text-ink2 hover:border-red-400 hover:text-red-600" aria-label="Delete" data-testid={`admin-delete-video-${v._id}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "requests" && (
          <div className="mt-8 space-y-3" data-testid="admin-requests-list">
            {requests === null && <p className="text-sm text-ink3 font-mono">Loading custom requests…</p>}
            {requests && requests.length === 0 && <p className="text-sm text-ink3 font-mono">No custom design requests yet.</p>}
            {requests && requests.map((r) => (
              <div key={r._id} className="card-surface p-5" data-testid={`admin-request-${r._id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-semibold">{r.name}</p>
                    <p className="font-mono text-[11px] text-ink3">{r.phone}{r.email ? ` · ${r.email}` : ""}</p>
                  </div>
                  <select value={r.status} onChange={(ev) => setRequestStatus(r._id, ev.target.value)} className="border border-line bg-surface px-3 py-2 font-mono text-xs outline-none focus:border-gold" data-testid={`admin-request-status-${r._id}`}>
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="quoted">Quoted</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="chip !text-[10px]">{r.jewellery_type}</span>
                  {r.style && <span className="chip !text-[10px]">{r.style}</span>}
                  {r.budget && <span className="chip !text-[10px]">{r.budget}</span>}
                  {r.occasion && <span className="chip !text-[10px]">{r.occasion}</span>}
                  {r.colours && <span className="chip !text-[10px]">{r.colours}</span>}
                </div>
                {r.message && <p className="mt-2 text-sm text-ink2 leading-relaxed">{r.message}</p>}
                <p className="mt-2 font-mono text-[10px] text-ink3">
                  {r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
                  {r.reference_image && (
                    <> · <a href={r.reference_image} target="_blank" rel="noopener noreferrer" className="text-gold underline" data-testid={`admin-request-ref-${r._id}`}>View reference image</a></>
                  )}
                </p>
              </div>
            ))}
          </div>
        )}

        {tab === "reviews" && (
          <div className="mt-8 space-y-3" data-testid="admin-reviews-list">
            {reviews === null && <p className="text-sm text-ink3 font-mono">Loading reviews…</p>}
            {reviews && reviews.length === 0 && <p className="text-sm text-ink3 font-mono">No reviews yet.</p>}
            {reviews && reviews.map((r) => (
              <div key={r._id} className="card-surface p-5" data-testid={`admin-review-${r._id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      {r.photo && (
                        <a href={r.photo.startsWith("http") ? r.photo : undefined} target="_blank" rel="noopener noreferrer">
                          <img src={r.photo} alt="Customer review photo" className="h-14 w-14 border border-line object-cover" data-testid={`admin-review-photo-${r._id}`} />
                        </a>
                      )}
                      <div>
                        <p className="font-display text-lg font-semibold">{r.name}</p>
                        <Rating value={r.rating} size={12} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`chip !text-[9px] ${r.approved ? "!text-gold" : "!text-red-600 !border-red-300"}`} data-testid={`admin-review-status-${r._id}`}>
                      {r.approved ? "Published" : "Pending approval"}
                    </span>
                    <button
                      onClick={() => setReviewApproval(r, !r.approved)}
                      className={`h-9 px-3 grid place-items-center border text-[10px] font-cinzel uppercase ${r.approved ? "border-line text-ink2 hover:border-gold hover:text-gold" : "border-gold bg-gold text-[#241A08] hover:opacity-90"}`}
                      style={{ letterSpacing: "0.16em" }}
                      data-testid={`admin-approve-review-${r._id}`}
                    >
                      {r.approved ? "Unpublish" : "Approve & publish"}
                    </button>
                    <button onClick={() => deleteReview(r)} className="h-9 w-9 grid place-items-center border border-line text-ink2 hover:border-red-400 hover:text-red-600" aria-label="Delete review" data-testid={`admin-delete-review-${r._id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                {r.text && <p className="mt-2 text-sm text-ink2 leading-relaxed">{r.text}</p>}
                <p className="mt-2 font-mono text-[10px] text-ink3">product {String(r.product_id).slice(-6)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <ProductForm initial={editing} onClose={() => setEditing(null)} onSave={saveProduct} />
      )}
      {editingVideo !== null && (
        <VideoForm
          products={products || []}
          initial={editingVideo}
          onClose={() => setEditingVideo(null)}
          onSave={saveVideo}
        />
      )}
    </div>
  );
}
